import { useEffect } from 'react';

import ContentLayout from '@/V2/components/layouts/content-layout';
import StrategyBlocks from '@/V2/features/brand-strategy/components/strategy-blocks';
import StrategySectionContent from '@/V2/features/brand-strategy/components/strategy-section-content';
import { useBrandStrategy } from '@/V2/features/brand-strategy/hooks/use-brand-strategy';
import { useBrandStrategyStore } from '@/V2/features/brand-strategy/store/brand-strategy-store';
import useProjectId from '@/hooks/useProjectId.ts';

type StrategySection =
  | 'customer-personas'
  | 'value-proposition'
  | 'getting-to-know-you'
  | 'tone-of-voice'
  | 'content-plan';

const BrandStrategy = () => {
  const {
    selected_section,
    setSelectedSection,
    setCustomerPersonas,
    setBrandStrategyId,
  } = useBrandStrategyStore();
  const projectId = useProjectId();
  const { data: brandStrategy, isLoading } = useBrandStrategy(projectId);

  // Update store with API data
  useEffect(() => {
    if (brandStrategy) {
      setBrandStrategyId(brandStrategy.id);
      setCustomerPersonas(brandStrategy.customer_personas ?? []);
    }
  }, [brandStrategy, setCustomerPersonas, setBrandStrategyId]);

  if (isLoading) {
    return (
      <ContentLayout title='Brand Strategy'>
        <div className='flex min-h-full w-full flex-col items-center justify-center'>
          <p className='text-gray-600'>Loading brand strategy...</p>
        </div>
      </ContentLayout>
    );
  }

  if (!brandStrategy) {
    return (
      <ContentLayout title='Brand Strategy'>
        <div className='flex min-h-full w-full flex-col items-center justify-center'>
          <p className='text-gray-600'>No brand strategy found</p>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout title='Brand Strategy'>
      <div className='flex min-h-full w-full flex-col'>
        <div className='mx-auto flex w-full items-start px-4 py-8 h-[calc(100vh-8rem)]'>
          <main className='flex-0 w-1/2 h-full overflow-y-auto pr-6 pb-8'>
            <StrategyBlocks
              projectId={projectId}
              onSectionSelect={setSelectedSection}
              selectedSection={selected_section as StrategySection}
            />
          </main>

          <div className='h-full w-px bg-[#DCDAD9] mx-12' />

          <aside className='w-1/2 shrink-0 xl:block h-full overflow-y-auto pb-8'>
            <StrategySectionContent
              selectedSection={selected_section as StrategySection}
              projectId={projectId}
            />
          </aside>
        </div>
      </div>
    </ContentLayout>
  );
};

export default BrandStrategy;
